import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PhygitalIcon from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  ArrowLeft,
  Scissors,
  Sparkles,
  Palette,
  Eye,
  PartyPopper,
  Save,
  Plus,
  X,
  Image,
  Upload,
  Check,
  AlertCircle,
  Share
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preference options for each category
const PREFERENCE_OPTIONS = {
  hair: {
    title: "Hair Preferences",
    icon: Scissors,
    color: "from-blue-500 to-teal-500",
    sections: [
      {
        id: "texture",
        label: "Hair Texture",
        type: "select",
        options: ["Straight", "Wavy", "Curly", "Coily", "Mixed"]
      },
      {
        id: "density",
        label: "Hair Density",
        type: "select",
        options: ["Fine/Thin", "Medium", "Thick/Coarse"]
      },
      {
        id: "length",
        label: "Current Length",
        type: "select",
        options: ["Pixie/Very Short", "Short", "Medium", "Long", "Very Long"]
      },
      {
        id: "color_treated",
        label: "Color Treated?",
        type: "toggle"
      },
      {
        id: "current_color",
        label: "Current Color",
        type: "text",
        placeholder: "e.g., Dark brown with highlights"
      },
      {
        id: "desired_style",
        label: "Desired Style",
        type: "textarea",
        placeholder: "Describe your ideal look or paste inspiration..."
      },
      {
        id: "sensitivities",
        label: "Scalp Sensitivities",
        type: "multi",
        options: ["Sensitive scalp", "Dry scalp", "Dandruff", "Product allergies", "None"]
      },
      {
        id: "avoid",
        label: "Products/Techniques to Avoid",
        type: "tags",
        placeholder: "Add items to avoid..."
      }
    ]
  },
  nails: {
    title: "Nail Preferences",
    icon: Sparkles,
    color: "from-pink-500 to-purple-500",
    sections: [
      {
        id: "nail_shape",
        label: "Preferred Shape",
        type: "select",
        options: ["Square", "Round", "Oval", "Almond", "Coffin/Ballerina", "Stiletto", "Squoval"]
      },
      {
        id: "nail_length",
        label: "Preferred Length",
        type: "select",
        options: ["Natural/Short", "Medium", "Long", "Extra Long"]
      },
      {
        id: "service_type",
        label: "Usual Service Type",
        type: "multi",
        options: ["Regular polish", "Gel polish", "Dip powder", "Acrylics", "Press-ons", "Nail art"]
      },
      {
        id: "style_preference",
        label: "Style Preference",
        type: "multi",
        options: ["Minimalist", "French tips", "Bold colors", "Nail art", "Glitter/Sparkle", "Matte finish", "Chrome/Metallic"]
      },
      {
        id: "favorite_colors",
        label: "Favorite Colors",
        type: "tags",
        placeholder: "Add your go-to colors..."
      },
      {
        id: "allergies",
        label: "Allergies/Sensitivities",
        type: "multi",
        options: ["Acrylic allergy", "Gel allergy", "Sensitive cuticles", "None"]
      },
      {
        id: "notes",
        label: "Special Requests",
        type: "textarea",
        placeholder: "Any specific requests for your nail tech..."
      }
    ]
  },
  makeup: {
    title: "Makeup Preferences",
    icon: Palette,
    color: "from-purple-500 to-indigo-500",
    sections: [
      {
        id: "skin_type",
        label: "Skin Type",
        type: "select",
        options: ["Dry", "Oily", "Combination", "Normal", "Sensitive"]
      },
      {
        id: "skin_tone",
        label: "Skin Tone",
        type: "select",
        options: ["Fair", "Light", "Medium", "Tan", "Deep"]
      },
      {
        id: "undertone",
        label: "Undertone",
        type: "select",
        options: ["Cool", "Warm", "Neutral", "Not sure"]
      },
      {
        id: "coverage",
        label: "Preferred Coverage",
        type: "select",
        options: ["Natural/No makeup look", "Light", "Medium", "Full glam"]
      },
      {
        id: "style",
        label: "Makeup Style",
        type: "multi",
        options: ["Natural", "Soft glam", "Bold/Dramatic", "Editorial", "Bridal", "Everyday"]
      },
      {
        id: "focus_areas",
        label: "Favorite Features to Highlight",
        type: "multi",
        options: ["Eyes", "Lips", "Cheekbones", "Brows", "Skin"]
      },
      {
        id: "allergies",
        label: "Allergies/Sensitivities",
        type: "tags",
        placeholder: "Add any product allergies..."
      },
      {
        id: "notes",
        label: "Special Requests",
        type: "textarea",
        placeholder: "Any specific preferences or requests..."
      }
    ]
  },
  lash: {
    title: "Lash Preferences",
    icon: Eye,
    color: "from-amber-500 to-orange-500",
    sections: [
      {
        id: "service_type",
        label: "Lash Service Type",
        type: "select",
        options: ["Classic extensions", "Volume extensions", "Hybrid", "Lash lift", "Strip lashes", "Not sure yet"]
      },
      {
        id: "lash_style",
        label: "Desired Style",
        type: "select",
        options: ["Natural", "Cat eye", "Doll eye", "Wispy", "Dramatic", "Custom"]
      },
      {
        id: "length_preference",
        label: "Length Preference",
        type: "select",
        options: ["Natural length", "Slightly longer", "Medium drama", "Full drama"]
      },
      {
        id: "curl_type",
        label: "Curl Preference",
        type: "select",
        options: ["J curl (subtle)", "B curl (natural)", "C curl (popular)", "D curl (dramatic)", "Not sure"]
      },
      {
        id: "eye_shape",
        label: "Eye Shape",
        type: "select",
        options: ["Almond", "Round", "Hooded", "Monolid", "Downturned", "Upturned", "Not sure"]
      },
      {
        id: "sensitivities",
        label: "Sensitivities",
        type: "multi",
        options: ["Sensitive eyes", "Contact lens wearer", "Glue sensitivity", "None"]
      },
      {
        id: "notes",
        label: "Additional Notes",
        type: "textarea",
        placeholder: "Any specific requests or concerns..."
      }
    ]
  },
  events: {
    title: "Event Preferences",
    icon: PartyPopper,
    color: "from-violet-500 to-purple-500",
    sections: [
      {
        id: "meal_preference",
        label: "Meal Preference",
        type: "select",
        options: ["Chicken", "Fish", "Steak/Beef", "Vegetarian", "Vegan", "No preference"]
      },
      {
        id: "dietary",
        label: "Dietary Restrictions",
        type: "multi",
        options: ["Vegetarian", "Vegan", "Gluten-free", "Dairy-free", "Nut allergy", "Shellfish allergy", "Kosher", "Halal", "None"]
      },
      {
        id: "food_allergies",
        label: "Food Allergies",
        type: "tags",
        placeholder: "List any specific food allergies..."
      },
      {
        id: "alcohol",
        label: "Alcohol Preference",
        type: "select",
        options: ["Yes, I drink", "Non-alcoholic only", "Wine only", "No preference"]
      },
      {
        id: "accessibility",
        label: "Accessibility Needs",
        type: "multi",
        options: ["Wheelchair accessible", "Elevator access", "Hearing assistance", "Visual assistance", "Quiet space needed", "Seating required", "None"]
      },
      {
        id: "conversation_style",
        label: "Conversation Style",
        type: "select",
        options: ["Love meeting new people", "Prefer small groups", "Happy to observe", "Focused networking"]
      },
      {
        id: "music_preference",
        label: "Music Preference",
        type: "multi",
        options: ["Pop", "R&B", "Hip-hop", "Electronic", "Jazz", "Rock", "Indie", "Classical", "Latin", "Afrobeats"]
      },
      {
        id: "interests",
        label: "Interests",
        type: "tags",
        placeholder: "Add your interests..."
      },
      {
        id: "notes",
        label: "Anything Else?",
        type: "textarea",
        placeholder: "Any other preferences for events..."
      }
    ]
  }
};

const PreferenceSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "hair";
  
  const [activeCategory, setActiveCategory] = useState(categoryParam);
  const [preferences, setPreferences] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [inspirationImages, setInspirationImages] = useState([]);

  const categories = Object.keys(PREFERENCE_OPTIONS);
  const currentCategory = PREFERENCE_OPTIONS[activeCategory];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem("session_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const res = await fetch(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreferences(data || {});
      }
    } catch (err) {
      console.error("Error fetching preferences:", err);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      
      const res = await fetch(`${API}/user/preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(preferences)
      });
      
      if (res.ok) {
        setSaved(true);
        toast.success("Preferences saved successfully!");
        setTimeout(() => setSaved(false), 2000);
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (err) {
      console.error("Error saving preferences:", err);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const shareProfile = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/profiles/${activeCategory}/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ expires_in_hours: 24 })
      });
      
      if (res.ok) {
        const data = await res.json();
        await navigator.clipboard.writeText(data.share_url);
        toast.success("Share link copied to clipboard!");
      }
    } catch (err) {
      toast.error("Could not generate share link");
    }
  };

  const updatePreference = (category, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const toggleMultiOption = (category, field, option) => {
    const current = preferences[category]?.[field] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    updatePreference(category, field, updated);
  };

  const addTag = (category, field, tag) => {
    if (!tag.trim()) return;
    const current = preferences[category]?.[field] || [];
    if (!current.includes(tag.trim())) {
      updatePreference(category, field, [...current, tag.trim()]);
    }
  };

  const removeTag = (category, field, tag) => {
    const current = preferences[category]?.[field] || [];
    updatePreference(category, field, current.filter(t => t !== tag));
  };

  const renderField = (section) => {
    const value = preferences[activeCategory]?.[section.id];
    
    switch (section.type) {
      case "select":
        return (
          <div className="flex flex-wrap gap-2">
            {section.options.map((option) => (
              <button
                key={option}
                onClick={() => updatePreference(activeCategory, section.id, option)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  value === option
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        );
      
      case "multi":
        return (
          <div className="flex flex-wrap gap-2">
            {section.options.map((option) => {
              const isSelected = (value || []).includes(option);
              return (
                <button
                  key={option}
                  onClick={() => toggleMultiOption(activeCategory, section.id, option)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1 ${
                    isSelected
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                  {option}
                </button>
              );
            })}
          </div>
        );
      
      case "toggle":
        return (
          <Switch
            checked={value || false}
            onCheckedChange={(checked) => updatePreference(activeCategory, section.id, checked)}
          />
        );
      
      case "text":
        return (
          <Input
            value={value || ""}
            onChange={(e) => updatePreference(activeCategory, section.id, e.target.value)}
            placeholder={section.placeholder}
            className="max-w-md"
          />
        );
      
      case "textarea":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => updatePreference(activeCategory, section.id, e.target.value)}
            placeholder={section.placeholder}
            className="max-w-lg"
            rows={3}
          />
        );
      
      case "tags":
        return (
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {(value || []).map((tag) => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button onClick={() => removeTag(activeCategory, section.id, tag)}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={section.placeholder}
                className="max-w-xs"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    addTag(activeCategory, section.id, e.target.value);
                    e.target.value = "";
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  const input = e.target.previousSibling;
                  addTag(activeCategory, section.id, input.value);
                  input.value = "";
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const getCategoryProgress = (cat) => {
    const catPrefs = preferences[cat] || {};
    const fields = PREFERENCE_OPTIONS[cat].sections.length;
    const filled = Object.keys(catPrefs).filter(k => {
      const val = catPrefs[k];
      return val && (Array.isArray(val) ? val.length > 0 : true);
    }).length;
    return Math.round((filled / fields) * 100);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="preference-setup-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/me")} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-semibold text-lg">Preference Profiles</h1>
                <p className="text-sm text-gray-600">Set up your preferences once, share anywhere</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={shareProfile}
                data-testid="share-profile-btn"
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button 
                onClick={savePreferences}
                disabled={saving}
                className={saved ? "bg-green-600 hover:bg-green-700" : ""}
                data-testid="save-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : saved ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saved ? "Saved!" : "Save All"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6" data-testid="category-tabs">
          {categories.map((cat) => {
            const config = PREFERENCE_OPTIONS[cat];
            const Icon = config.icon;
            const progress = getCategoryProgress(cat);
            
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`category-tab-${cat}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? "bg-gray-900 text-white"
                    : "bg-white border border-gray-200 text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="capitalize">{cat}</span>
                {progress > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeCategory === cat ? "bg-white/20" : "bg-gray-100"
                  }`}>
                    {progress}%
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Category Header */}
        <Card className="mb-6 overflow-hidden">
          <div className={`h-2 bg-gradient-to-r ${currentCategory.color}`} />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentCategory.color} flex items-center justify-center`}>
                <currentCategory.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle>{currentCategory.title}</CardTitle>
                <p className="text-sm text-gray-600">
                  {getCategoryProgress(activeCategory)}% complete
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Preference Fields */}
        <div className="space-y-6">
          {currentCategory.sections.map((section) => (
            <Card key={section.id}>
              <CardContent className="pt-6">
                <label className="font-medium mb-3 block">{section.label}</label>
                {renderField(section)}
              </CardContent>
            </Card>
          ))}

          {/* Inspiration Images - Only for beauty categories, not events */}
          {activeCategory !== 'events' && (
            <Card>
              <CardContent className="pt-6">
                <label className="font-medium mb-3 block flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Inspiration Images
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Add photos for your stylist to reference
                </p>
                
                <div className="grid grid-cols-3 gap-3">
                  {inspirationImages.map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-lg bg-gray-100 relative group">
                      <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                      <button 
                        onClick={() => setInspirationImages(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                  
                  <button className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                    <Upload className="w-6 h-6 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-600">Add Photo</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Save Button Fixed at Bottom on Mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 sm:hidden">
          <Button 
            onClick={savePreferences}
            disabled={saving}
            className={`w-full ${saved ? "bg-green-600 hover:bg-green-700" : ""}`}
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : saved ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saved ? "Saved!" : "Save All Preferences"}
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-gray-100 bg-white mt-8 mb-16 sm:mb-0">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-2 text-sm text-gray-600">
          <PhygitalIcon className="w-5 h-5" />
          <span>Your preferences, perfectly communicated</span>
        </div>
      </footer>
    </div>
  );
};

export default PreferenceSetupPage;
